import React from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box, Button, Divider, Drawer, IconButton, Stack, Typography } from '@mui/material';
import type { FeatureFlagDrawerProps } from '../../../types/features/featureFlags';
import { adminColors, adminRadii, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminButtonSx,
  adminButtonToneSx,
  adminCombineSx,
  adminIconActionButtonSx,
} from '../../../styles/adminComponentStyles';

const FeatureFlagDrawer: React.FC<FeatureFlagDrawerProps> = ({
  children,
  mode,
  onClose,
  onPrimaryAction,
  open,
  primaryButtonDisabled = false,
  submitting = false,
}) => {
  const isCreateMode = mode === 'create';
  const title = isCreateMode ? 'Create Feature' : 'Update Feature';
  const primaryButtonText = isCreateMode ? 'Create Feature' : 'Save Changes';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: `${adminRadii.md} 0 0 ${adminRadii.md}` },
          maxWidth: '100vw',
          width: { xs: '100%', sm: 520 },
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            backgroundColor: adminColors.surface,
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
            <Typography sx={{ ...adminTypography.sectionTitle, color: adminColors.text }}>
              {title}
            </Typography>
            <IconButton aria-label="Close feature drawer" size="small" onClick={onClose} sx={adminIconActionButtonSx}>
              <CloseRoundedIcon fontSize="inherit" />
            </IconButton>
          </Stack>
          <Divider />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {children}
        </Box>

        <Box
          sx={{
            backgroundColor: adminColors.surface,
            bottom: 0,
            position: 'sticky',
            zIndex: 1,
          }}
        >
          <Divider />
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ px: 2, py: 1.5 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={onClose}
              disabled={submitting}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary, { minWidth: 96 })}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={onPrimaryAction}
              disabled={submitting || primaryButtonDisabled}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.primary, { minWidth: 132 })}
            >
              {submitting ? 'Saving...' : primaryButtonText}
            </Button>
          </Stack>
        </Box>
      </Box>
    </Drawer>
  );
};

export default React.memo(FeatureFlagDrawer);
